// Manifest builders — typed factories so callsites don't hand-roll
// `{ version, source, palette, seed }`. One overload per entity class.
//
// Each builder produces a v2 PatternManifest with a synthetic source
// when the entity has no uploaded URL, or a URL source when it does.
// Palette + seed are derived from the entity's stable identifiers
// (userId / pubkey / outcomeIndex) so the rendered output is identical
// across refreshes, devices, and visitors.

import type { OutcomeHueName } from '@/config/outcome-colors';
import { hashStringToSeed } from '../core/derive';
import { pickBrandBluePalette } from '../presets/brand-blue-palettes';
import {
  AVATAR_PLACEHOLDER_VARIANT,
  COVER_PLACEHOLDER_VARIANT,
  OUTCOME_PLACEHOLDER_VARIANT,
  resolveCategoryPlaceholderSource,
} from '../presets/placeholder-sources';
import type { ManifestSource, PatternManifest } from './types';
import { MANIFEST_VERSION } from './types';

export interface ManifestForUserArgs {
  /** Stable, immutable user identifier. Drives both palette and seed. */
  readonly userId: string;
  /** Uploaded avatar URL (Pinata / OAuth). null/empty → synthetic placeholder. */
  readonly avatarUrl: string | null | undefined;
}

export function manifestForUser(args: ManifestForUserArgs): PatternManifest {
  const seed = hashStringToSeed(args.userId);
  const source: ManifestSource =
    typeof args.avatarUrl === 'string' && args.avatarUrl.length > 0
      ? { kind: 'url', url: args.avatarUrl }
      : { kind: 'synthetic', variant: AVATAR_PLACEHOLDER_VARIANT, seed };
  return {
    version: MANIFEST_VERSION,
    source,
    palette: { kind: 'preset', name: pickBrandBluePalette(args.userId) },
    seed,
  };
}

export interface ManifestForUserCoverArgs {
  readonly userId: string;
  /** Pinata URL of the user's chosen / uploaded cover. null/empty → synthetic. */
  readonly coverUrl: string | null | undefined;
}

export function manifestForUserCover(args: ManifestForUserCoverArgs): PatternManifest {
  const seed = hashStringToSeed(args.userId);
  const source: ManifestSource =
    typeof args.coverUrl === 'string' && args.coverUrl.length > 0
      ? { kind: 'url', url: args.coverUrl }
      : { kind: 'synthetic', variant: COVER_PLACEHOLDER_VARIANT, seed };
  return {
    version: MANIFEST_VERSION,
    source,
    palette: { kind: 'preset', name: pickBrandBluePalette(args.userId) },
    seed,
  };
}

export interface ManifestForMarketArgs {
  /** Solana market pubkey (base58). Drives the seed for per-market jitter. */
  readonly pubkey: string;
  readonly category: string;
  readonly coverImageUrl: string | null | undefined;
}

export function manifestForMarket(args: ManifestForMarketArgs): PatternManifest {
  const pubkeyHash = hashStringToSeed(args.pubkey);
  const placeholder = resolveCategoryPlaceholderSource(args.category);
  // Normalise empty / missing category to "other" so the encoded manifest
  // hash is stable across callsites that pass `''` vs `'other'` for the
  // same render.
  const category = args.category.length > 0 ? args.category : 'other';
  const source: ManifestSource =
    typeof args.coverImageUrl === 'string' && args.coverImageUrl.length > 0
      ? { kind: 'url', url: args.coverImageUrl }
      : {
          kind: 'synthetic',
          variant: placeholder.variant,
          seed: (placeholder.baseSeed ^ pubkeyHash) >>> 0,
        };
  return {
    version: MANIFEST_VERSION,
    source,
    palette: { kind: 'category', category },
    seed: pubkeyHash,
  };
}

export interface ManifestForOutcomeArgs {
  readonly marketPubkey: string;
  readonly outcomeIndex: number;
  /** Hue assigned to this outcome by the canonical outcome-color hook. */
  readonly hue: OutcomeHueName;
  readonly imageUrl: string | null | undefined;
}

export function manifestForOutcome(args: ManifestForOutcomeArgs): PatternManifest {
  const seed = hashStringToSeed(`${args.marketPubkey}:${String(args.outcomeIndex)}`);
  const source: ManifestSource =
    typeof args.imageUrl === 'string' && args.imageUrl.length > 0
      ? { kind: 'url', url: args.imageUrl }
      : { kind: 'synthetic', variant: OUTCOME_PLACEHOLDER_VARIANT, seed };
  return {
    version: MANIFEST_VERSION,
    source,
    palette: { kind: 'outcome-hue', hue: args.hue },
    seed,
  };
}

export interface ManifestForWinnerArgs {
  readonly mint: string;
  readonly hue: OutcomeHueName;
  readonly imageUrl: string | null | undefined;
}

export function manifestForWinner(args: ManifestForWinnerArgs): PatternManifest {
  const seed = hashStringToSeed(args.mint);
  const source: ManifestSource =
    typeof args.imageUrl === 'string' && args.imageUrl.length > 0
      ? { kind: 'url', url: args.imageUrl }
      : { kind: 'synthetic', variant: OUTCOME_PLACEHOLDER_VARIANT, seed };
  return {
    version: MANIFEST_VERSION,
    source,
    palette: { kind: 'outcome-hue', hue: args.hue },
    seed,
  };
}
